/**
 * Workshop layout provider — supplies the same context shape as the
 * production LayoutProvider but without any backend dependencies.
 *
 * Provides sidebar/right-panel open state and active tab tracking.
 */

import { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";

/* ---------- Types (mirroring production) ---------- */

type SidebarTab = "frames" | "browse";
type RightPanelTab = "details" | "logs" | "actions";

interface LayoutContextValue {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  rightPanelOpen: boolean;
  setRightPanelOpen: (open: boolean) => void;
  toggleRightPanel: () => void;
  sidebarTab: SidebarTab;
  setSidebarTab: (tab: SidebarTab) => void;
  rightPanelTab: RightPanelTab;
  setRightPanelTab: (tab: RightPanelTab) => void;
}

/* ---------- Context ---------- */

const WorkshopLayoutContext = createContext<LayoutContextValue | null>(null);

export function useWorkshopLayout(): LayoutContextValue {
  const ctx = useContext(WorkshopLayoutContext);
  if (!ctx) {
    throw new Error(
      "useWorkshopLayout must be used within WorkshopLayoutProvider",
    );
  }
  return ctx;
}

/* ---------- Provider ---------- */

interface WorkshopLayoutProviderProps {
  children: ReactNode;
  defaultSidebarOpen?: boolean;
  defaultRightPanelOpen?: boolean;
}

export function WorkshopLayoutProvider({
  children,
  defaultSidebarOpen = true,
  defaultRightPanelOpen = false,
}: WorkshopLayoutProviderProps) {
  const [sidebarOpen, setSidebarOpen] = useState(defaultSidebarOpen);
  const [rightPanelOpen, setRightPanelOpen] = useState(
    defaultRightPanelOpen,
  );
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("frames");
  const [rightPanelTab, setRightPanelTab] =
    useState<RightPanelTab>("details");

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const toggleRightPanel = useCallback(() => {
    setRightPanelOpen((prev) => !prev);
  }, []);

  const value: LayoutContextValue = {
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar,
    rightPanelOpen,
    setRightPanelOpen,
    toggleRightPanel,
    sidebarTab,
    setSidebarTab,
    rightPanelTab,
    setRightPanelTab,
  };

  return (
    <WorkshopLayoutContext.Provider value={value}>
      {children}
    </WorkshopLayoutContext.Provider>
  );
}

export type {
  LayoutContextValue,
  SidebarTab,
  RightPanelTab,
  WorkshopLayoutProviderProps,
};
